import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Undo2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const kind = (r) => (r.type === "adjust" ? "تعديل المتبقي" : r.payroll_month ? `خصم من راتب ${r.payroll_month}` : r.method === "bank" ? "سداد بتحويل" : "سداد نقدي");

export const AdvanceRepaymentsDialog = ({ adv, onClose, onChanged, canEdit }) => {
  const [a, setA] = useState(adv), [val, setVal] = useState(""), [note, setNote] = useState(""), [busy, setBusy] = useState("");
  useEffect(() => { setA(adv); setVal(adv ? String(adv.outstanding) : ""); setNote(""); }, [adv]);
  if (!a) return null;
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); setA(r); setVal(String(r.outstanding)); toast.success(msg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); } finally { setBusy(""); }
  };
  const undo = (r) => {
    const warn = r.payroll_month ? `\nهذا السداد خُصم من راتب ${r.payroll_month} — التراجع لا يعدّل كشف الراتب.` : r.journal_number ? `\nسيُعكس القيد ${r.journal_number}.` : "";
    if (window.confirm(`التراجع عن ${kind(r)} بمبلغ ${fmt(r.amount)}؟${warn}`)) run(r.id, () => api.undoAdvanceRepayment(a.id, r.id), "تم التراجع");
  };
  const reps = [...(a.repayments || [])].reverse();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" aria-describedby={undefined} className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="advance-repayments-dialog">
        <DialogHeader className="text-start"><DialogTitle>سدادات السلفة — {a.employee_name}</DialogTitle>
          <DialogDescription>المبلغ {fmt(a.amount)} · المسدد {fmt(a.paid)} · المتبقي <b data-testid="advance-rep-outstanding">{fmt(a.outstanding)}</b></DialogDescription></DialogHeader>
        {canEdit && (
          <div className="space-y-2 rounded-xl border border-violet-200 bg-violet-50 p-3" data-testid="advance-edit-outstanding">
            <p className="text-xs font-semibold text-violet-800">تعديل المتبقي</p>
            <div className="flex gap-2">
              <Input type="number" dir="ltr" value={val} onChange={(e) => setVal(e.target.value)} className="h-9 w-32" data-testid="advance-outstanding-input" />
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="السبب (اختياري)" className="h-9" data-testid="advance-outstanding-note" />
              <Button size="sm" className="h-9 shrink-0 bg-violet-600 hover:bg-violet-700" disabled={busy === "set" || val === "" || Number(val) === a.outstanding}
                onClick={() => run("set", () => api.setAdvanceOutstanding(a.id, Number(val), note), "تم تعديل المتبقي")} data-testid="advance-outstanding-save">
                {busy === "set" ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ"}</Button>
            </div>
          </div>
        )}
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {!reps.length && <p className="p-4 text-center text-sm text-slate-400">لا توجد سدادات</p>}
          {reps.map((r) => (
            <div key={r.id} className="flex items-center gap-2 p-2.5 text-sm" data-testid={`advance-rep-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{kind(r)} <span className="font-normal text-slate-400">· {r.date}</span></p>
                <p className="truncate text-xs text-slate-500">{r.note || ""}{r.journal_number ? ` · قيد ${r.journal_number}` : ""}{r.created_by ? ` · ${r.created_by}` : ""}</p>
              </div>
              <span className={`font-bold tabular-nums ${r.amount < 0 ? "text-rose-600" : "text-emerald-700"}`}>{r.amount < 0 ? "+" : "−"}{fmt(Math.abs(r.amount))}</span>
              {canEdit && <Button size="sm" variant="ghost" className="h-8 text-rose-600" disabled={busy === r.id} onClick={() => undo(r)} data-testid={`advance-rep-undo-${r.id}`}>
                {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} تراجع</Button>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
